import type { EnterKeyBehavior } from '@web/api/types'
import type { KeyboardEvent } from 'react'

export type SendAction = 'primary' | 'alternate'
export type ComposerSendMode = 'send' | 'queue' | 'steer'
export const ENTER_KEY_BEHAVIORS: { value: EnterKeyBehavior; label: string }[] =
  [
    { value: 'standard', label: 'Standard' },
    { value: 'eager', label: 'Eager' },
    { value: 'pro', label: 'Pro' },
    { value: 'eager_pro', label: 'Eager Pro' },
  ]

export function macKeyboard(): boolean {
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform)
}

/** Null leaves text editing to the browser, including Shift+Enter and IME confirmation. */
export function enterKeyAction(
  mode: EnterKeyBehavior,
  event: {
    key: string
    shiftKey?: boolean
    metaKey?: boolean
    ctrlKey?: boolean
    altKey?: boolean
    isComposing?: boolean
    keyCode?: number
    repeat?: boolean
  },
  mac: boolean,
): SendAction | null {
  if (
    event.key !== 'Enter' ||
    event.shiftKey ||
    event.isComposing ||
    event.keyCode === 229 ||
    event.repeat
  )
    return null
  const modifier = mac ? event.metaKey : event.ctrlKey
  // Unadvertised modifier combinations remain available for normal text editing.
  if (mac ? event.ctrlKey : event.metaKey) return null
  if (mode === 'pro' || mode === 'eager_pro')
    return modifier ? (event.altKey ? 'alternate' : 'primary') : null
  if (event.altKey) return null
  return modifier ? 'alternate' : 'primary'
}

/** Idle messages send immediately; startup can only queue; eager modes steer open turns. */
export function composerSendMode(
  mode: EnterKeyBehavior,
  action: SendAction,
  running: boolean,
  waking = false,
): ComposerSendMode {
  if (waking) return 'queue'
  if (!running) return 'send'
  const eager = mode === 'eager' || mode === 'eager_pro'
  return eager !== (action === 'alternate') ? 'steer' : 'queue'
}

export function enterKeyDescription(
  mode: EnterKeyBehavior,
  mac: boolean,
): string[] {
  const modifier = mac ? '⌘' : 'Ctrl'
  const eager = mode === 'eager' || mode === 'eager_pro'
  const primary = eager ? 'Send/Steer' : 'Send/Queue'
  const alternate = eager ? 'Queue' : 'Steer'
  return mode === 'pro' || mode === 'eager_pro'
    ? [
        'Enter for New Line · Shift+Enter for New Line',
        `${modifier}+Enter to ${primary}`,
        `${modifier}+Alt+Enter to ${alternate}`,
      ]
    : [
        `Enter to ${primary}`,
        'Shift+Enter for New Line',
        `${modifier}+Enter to ${alternate}`,
      ]
}

/** Insert Shift+Enter explicitly: browsers may reserve modified Enter instead of inserting text. */
export function handleComposerEnter(
  event: KeyboardEvent<HTMLTextAreaElement>,
  mode: EnterKeyBehavior,
  onText: (text: string) => void,
  onSend: (action: SendAction) => void,
): void {
  if (
    event.key === 'Enter' &&
    event.shiftKey &&
    !event.nativeEvent.isComposing &&
    event.nativeEvent.keyCode !== 229
  ) {
    event.preventDefault()
    const field = event.currentTarget
    const start = field.selectionStart
    const end = field.selectionEnd
    onText(`${field.value.slice(0, start)}\n${field.value.slice(end)}`)
    requestAnimationFrame(() => {
      if (field.isConnected) field.setSelectionRange(start + 1, start + 1)
    })
    return
  }
  const action = enterKeyAction(mode, event.nativeEvent, macKeyboard())
  if (action) {
    event.preventDefault()
    onSend(action)
  }
}
